import { WizDividerChildContent } from './../../../obj/layouts/wiz-divider-child-content';
import { DOCUMENT } from '@angular/common';
import { WizComponentUtils } from './../../../utils/wiz-component.utils';
import { WizGrabberComponent, WizGrabberEvent } from './grabber/wiz-grabber.component';
import { WizComponentManager } from './../../../services/core/component/wiz-component.manager';
import { ElementRef, Renderer2, ChangeDetectorRef, DoCheck, ViewContainerRef, ComponentRef,
  ViewChild, AfterViewInit, Directive, Inject, AfterContentInit, OnDestroy } from '@angular/core';
import { WizBaseLayout } from '../../core/wiz-base-layout';
import { BehaviorSubject } from 'rxjs';
/**
 * Classe de base pour les dividers
 * La seule chose que vont gérer les implémentations sera la direction
 */
@Directive()
export abstract class WizDividerBase extends WizBaseLayout implements DoCheck, AfterViewInit, AfterContentInit, OnDestroy {

  /** Le container physique */
  @ViewChild('container', {static: true, read: ViewContainerRef}) container: ViewContainerRef;

  /**
   * Propriété css Flex à utiliser selon le navigateur
   */
  private _flex: string;

  /**
   * Propriété css flex basis à utiliser selon le navigateur
   */
  private _flexBasis: string;

  /**
   * Propriété css flex shrink à utiliser selon le navigateur
   */
  private _flexShrink: string;

  /**
   * Liste de grabbers ajoutés dans le composant
   */
  private _grabbers: ComponentRef<WizGrabberComponent>[] = [];

  /**
   * Flag passant à true à la première utiisation d'un des grabbers.
   * A partir de là, tous les enfants passent en mode flex-basis,
   * leur redimensionnement est alors à gérer en interne
   */
  private _hasGrabbed: boolean = false;

  /**
   * Liste des éléments (hors grabbers) ajoutés dans le composant
   */
  private _dividedElements: WizDividerChildContent[] = [];

  constructor(public elementRef: ElementRef,
              public vcRef: ViewContainerRef,
              public  renderer: Renderer2,
              protected cdref: ChangeDetectorRef,
              @Inject(DOCUMENT) protected document: Document) {
    super(elementRef, renderer, cdref);
  }

  /**
   * Ajout des grabbers
   */
  ngAfterContentInit(): void {
    this.initContent();
  }

  /**
   * Retourne la direction du divider divider: 'H' pour horizontal ou 'V' pour vertical
   */
  abstract get direction(): 'H' | 'V';

  /** Flag divider horizontal. Vertical si faux, donc. */
  private get isHDivider(): boolean { return this.direction === 'H'; }

  /*
   * Détection de l'injection d'éléments enfants
   */
  ngDoCheck(): void {
    // Si le nombre d'enfants a changé
    if (this.children && this.children.length !== this.numChildren) {
      this.numChildren = this.children.length;
      this.applyGap();
    }
  }

  /**
   * Pose du listener de redimensionnement global
   * Initialisation des classes flex à utiliser selon le navigateur
   */
  ngAfterViewInit() {

    this._flex = 'flex' in this.document.documentElement.style ? 'flex' :
    'webkitFlex' in this.document.documentElement.style ? 'webkitFlex' :
    'msFlexbox' in this.document.documentElement.style ? 'msFlexbox' : 'flex';

    this._flexBasis = 'flexBasis' in this.document.documentElement.style ? 'flexBasis' :
    'webkitFlexBasis' in this.document.documentElement.style ? 'webkitFlexBasis' :
    'msFlexPreferredSize' in this.document.documentElement.style ? 'msFlexPreferredSize' : 'flexBasis';

    this._flexShrink = 'flexShrink' in this.document.documentElement.style ? 'flexShrink' :
    'webkitFlexShrink' in this.document.documentElement.style ? 'webkitFlexShrink' :
    'msFlexNegative' in this.document.documentElement.style ? 'msFlexNegative' : 'flexShrink';
    this.listenToResize();

  }

  /** Ajout des poignées de redimensionnement */
  initContent() {
    // prise du nombre d'enfants
    const numElements: number = this.numChildren;
    // Initialisation de l'observable qui va permettre de réagir aux drags des grabbers
    const grabSubject: BehaviorSubject<WizGrabberEvent> = new BehaviorSubject<WizGrabberEvent>({});
    grabSubject.subscribe((event) => {
      this.onGrab(event);
    });
    // ajout des grabbers après chaque enfant, sauf le dernier.
    // à la fin, j'ai donc numElements * 2 - 1 enfants....
    // TODO : afficher ou pas les grabbers
    // TODO gestion minWidth/Height en directive
    for (let idx = 0; idx < numElements; idx++) {
      const elem = this.children[idx * 2]; // donc * 2
      // Initialisation et stockage des WizDividerChildContent qui nous faciliteront la gestion des éléments enfants
      const wizElem: WizDividerChildContent = new WizDividerChildContent(elem, this.direction);
      this._dividedElements.push(wizElem);
      // Ajout dynamique des grabbers
      if (idx < numElements - 1) {
        this.numChildren++;
        const grabber: ComponentRef<WizGrabberComponent> =
          WizComponentManager.instance.addChildTo(WizGrabberComponent, this.container, {
            index: 0,
            instanceProperties: {
              previousContent: wizElem,
              direction: this.direction,
              grabSubject
            }
          });

        this.elementRef.nativeElement.insertBefore(grabber.location.nativeElement, elem.nextElementSibling);
        this._grabbers.push(grabber);
      }
    }
  }

  /**
   * Gestion du gap
   */
  applyGap() {
    super.applyGap();

    // Conversion du gap en marges css
    // TODO LMA : perte de taille dispo à impacter sur la taille ventilée aux enfants
    for (const element of this.children) {

      if (this.isHDivider) {
        if (this.numChildren === 1) {
          this.renderer.setStyle(element, 'margin', '0px ');
        } else if (element === this.children[0]) {
          this.renderer.setStyle(element, 'margin-right', this.gap / 2 + 'px');
        } else if (element === this.children[(this.children.length - 1)]) {
          this.renderer.setStyle(element, 'margin-left', this.gap / 2 + 'px');
        } else {
          this.renderer.setStyle(element, 'margin', '0px ' + this.gap / 2 + 'px');
        }
      } else {
        if (this.numChildren === 1) {
          this.renderer.setStyle(element, 'margin', '0px');
        } else if (element === this.children[0] &&  this.numChildren > 1) {
          this.renderer.setStyle(element, 'margin-bottom', this.gap / 2 + 'px');
        } else if (element === this.children[(this.children.length - 1)]) {
          this.renderer.setStyle(element, 'margin-top', this.gap / 2 + 'px');
        } else {
          this.renderer.setStyle(element, 'margin',  this.gap / 2 + 'px 0px' );
        }
      }

      // Forcer les facteurs de retrécissement enfants non définis à 1
      if (element.style.flexShrink === '') {
        this.renderer.setStyle(element, 'flex-shrink', 1);
      }
    }
  }

  /**
   * Observation de redimensionnement venant d'un grabber
   * @param pEvent Contient les infos du grabber et de son mouvement
   */
  private onGrab(pEvent:WizGrabberEvent) {
    if (pEvent && pEvent.offset && pEvent.grabber && pEvent.grabber.previousContent) {
      // 1er grab, tous les enfants passent en mode 'flex-basis'
      if (!this._hasGrabbed) {
        this._dividedElements.forEach(content => {
          const coords: DOMRect = WizComponentUtils.getComponentCoordinates(content.getElement());
          this.renderer.removeStyle(content.getElement(), this._flex);
          if (this.isHDivider) {
            content.setSize(coords.width);
          } else {
            content.setSize(coords.height);
          }
          this.renderer.setStyle(content.getElement(), this._flexShrink, 0);
          this.renderer.setStyle(content.getElement(), this._flexBasis, content.getSize() + 'px');
        });
        this._hasGrabbed = true;
      }
      // L'enfant qui précède le grabber
      let previousElement: WizDividerChildContent = pEvent.grabber.previousContent;
      // L'enfant qu suit le grabber
      let i: number = this._dividedElements.indexOf(previousElement);
      let siblingElement: WizDividerChildContent = this._dividedElements[++i];
      // Taille max distribuable entre les 2
      const max: number = pEvent.grabber.previousContent.getSize() + siblingElement.getSize();
      // Si le mouvement permet le redimensionnement, application des nouvelles tailles
      let wrongElement: WizDividerChildContent = null;
      // On recherche les 2 éléments à redimensionner. Par défaut, les 2 autour du grabber.
      // Si un des 2 atteint sa min width, on va chercher à le remplacer par les suivant (ou le précédent, selon le sens)
      do {
        // Les 2 éléments peuvent être redimensionnés? si oui pas de retour, sinon celui qui ne peut pas est retourné
        wrongElement = this.checkMove(previousElement, siblingElement, pEvent.offset);
        // Si c'est à cause du max size atteint, on arrête tout. On ne n'essaye pas de repousser derrière
        if (wrongElement && !wrongElement.maxSizeReached) {
          i = this._dividedElements.indexOf(wrongElement); // L'indice de l'élément immobilisé
          // Si c'est l'élément qui précède, on cherche à le remplacer par son propre prédédent
          if (wrongElement === previousElement) {
            i--;
            if (i >= 0) {
              previousElement = this._dividedElements[i];
              wrongElement = this.checkMove(previousElement, siblingElement, pEvent.offset);
            }
            // Si c'est l'élément qui suit, on cherche à le remplacer par son propre suivant
          } else{
            i++;
            if (i < this._dividedElements.length) {
              siblingElement = this._dividedElements[i];
              wrongElement = this.checkMove(previousElement, siblingElement, pEvent.offset);
            }
          }
        }
      }
      while (wrongElement && !wrongElement.maxSizeReached && i > 0 && i < this._dividedElements.length);
      // On a trouvé les éléments aptes à se redimensionner, on redimensionne
      if (!wrongElement) {
        previousElement.setSize(previousElement.getSize() - pEvent.offset);
        siblingElement.setSize(siblingElement.getSize() + pEvent.offset);
        this.renderer.setStyle(previousElement.getElement(), this._flexBasis, previousElement.getSize() + 'px');
        this.renderer.setStyle(siblingElement.getElement(), this._flexBasis, siblingElement.getSize() + 'px');
      }


      // if (this.willFit((previousElement.getSize() - pEvent.offset), max, previousElement.getMinSize(), siblingElement.getMinSize())) {
      //   previousElement.setSize(previousElement.getSize() - pEvent.offset);
      //   siblingElement.setSize(siblingElement.getSize() + pEvent.offset);
      //   this.renderer.setStyle(previousElement.getElement(), this._flexBasis, previousElement.getSize() + 'px');
      //   this.renderer.setStyle(siblingElement.getElement(), this._flexBasis, siblingElement.getSize() + 'px');
      // }
    }
  }


  checkMove(pPreviousElement: WizDividerChildContent, pSiblingElement: WizDividerChildContent, pOffset: number): WizDividerChildContent {
    const newPreviousSize: number = pPreviousElement.getSize() - pOffset;
    const newSiblingSize: number = pSiblingElement.getSize() + pOffset;
    pPreviousElement.maxSizeReached = pSiblingElement.maxSizeReached = false;
    if (pOffset < 0) {
      if (pPreviousElement.getMaxSize() > 0 && pPreviousElement.getMaxSize() < newPreviousSize) {
        pPreviousElement.maxSizeReached = true;
        return pPreviousElement;
      } else if (pSiblingElement.getMinSize() > newSiblingSize) {
        return pSiblingElement;
      }
    } else {
      if (pSiblingElement.getMaxSize() > 0 && pSiblingElement.getMaxSize() <= newSiblingSize) {
        pSiblingElement.maxSizeReached = true;
        return pSiblingElement;
      } else if (pPreviousElement.getMinSize() > newPreviousSize) {
        return pPreviousElement;
      }
    }
    return null;
  }

  /**
   * Determine si la nouvelle valeur est applicable sans sortir du layout
   * @param newValue La nouvelle valeur pour le composant A
   * @param max Le total max de dimension attribuable.
   * @param minA Dimension minimale du composant A
   * @param minB Dimension minimale du composant B
   */
  willFit(newValue: number, max: number, minA: number, minB: number): boolean {
    // Pas de dimension minimale, limitation du total max seul
    if (!minA && !minB) {
      return newValue < max && newValue > 0;
    }
    // Limitation de la taille minimale du composant
    if (newValue < minA) {
      return false;
    }
    // Limitation de la taille minimale du composant adjacent
    if ((max - newValue) <= minB) {
      return false;
    }
    return true;
  }

  private _resizeObs: ResizeObserver;

  /**
   * Ecoute de l'évènement resize
   * Listen resize event and call the onResize function
   */
  private listenToResize(): void {
    this._resizeObs = new ResizeObserver((entries: ResizeObserverEntry[], observer) => {
      // console.log('divider resize', this.direction, entries);
      this.onResize(entries[0].contentRect);
    })

    this._resizeObs.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy() {
    this._resizeObs.disconnect();
  }

  /**
   * Handler de resize
   */
  private onResize(contentRect: {width: number, height: number}) {
    // La nouvelle largeur du divider
    const currentSize: number = this.isHDivider ? contentRect.width : contentRect.height;
    /* Si on n'a pas encore grabbé, on laisse faire la flexbox.
    Dans le cas contraire, il faut actualiser ici la dimension des enfants */
    if (this._hasGrabbed) {
      // Détermination de la dimension totale et de la dimension totale minimale des enfants
      let totalChildrenMinSize:number = 0;
      let totalElementSize:number = 0;
      this._dividedElements.forEach(element => {
        totalChildrenMinSize += element.getTotalMinSize()
        totalElementSize += element.getTotalSize();
      })
      this._grabbers.forEach(grabber => {
        totalChildrenMinSize += grabber.instance.totalSize;
        totalElementSize += grabber.instance.totalSize;
      })
      // Si la taille globale est différente de la taille totale des éléments, on retaille
      if (currentSize !== totalElementSize) {
        // Tout est distribué, on applique les tailles
        this._dividedElements.forEach(content => {
          const ratio = content.getSize() / totalElementSize;
          const proposedSize = currentSize * ratio;
          const newSize = proposedSize < content.getMinSize() ? content.getMinSize() :
                            proposedSize > content.getMaxSize() ? content.getMaxSize() : proposedSize;
          content.setNextSize(newSize);
          content.setSize(newSize);
          this.renderer.setStyle(content.getElement(), this._flexBasis, content.getSize() + 'px');
        })
      }
    }
  }
}

